import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { LogOut, Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { API, useAdminAuth } from '../context/AdminAuthContext';

/**
 * Feb 2026 — DeskButton
 *
 * Compact dashboard widget for the "Leave Desk / Return to Desk"
 * flow. Polls `/admin/desk/current` on mount to figure out whether
 * the user is off-duty (button disabled), has an open desk record
 * (Return button), or is at their desk (Leave button + reason
 * picker modal). Callers can pass `onChanged` to refresh their
 * own state after a successful transition.
 */
/**
 * Feb 2026 — DeskButton
 *
 * Compact dashboard widget for the "Leave Desk / Return to Desk"
 * flow. Polls `{basePath}/desk/current` on mount to figure out
 * whether the user is off-duty (button disabled), has an open desk
 * record (Return button), or is at their desk (Leave button +
 * reason picker modal). Callers can pass `onChanged` to refresh
 * their own state after a successful transition.
 *
 * Works for BOTH admins and branch users:
 *   • Admin usage (default): mounted with no props →
 *     uses `useAdminAuth` headers + `/admin/desk/…` endpoints.
 *   • Branch usage: pass `authHeaders` + `basePath="/branch"`
 *     so it targets `/branch/desk/…` with the branch JWT.
 */
const CATEGORY_OPTIONS = [
  { key: 'prayer',       label: 'Prayer' },
  { key: 'lunch',        label: 'Lunch' },
  { key: 'meeting',      label: 'Meeting' },
  { key: 'personal',     label: 'Personal' },
  { key: 'cash_deposit', label: 'Cash Deposit' },
  { key: 'other',        label: 'Other' },
];

const DeskButton = ({ compact = false, onChanged, basePath, authHeaders: passedHeaders }) => {
  const adminAuth = useAdminAuth();
  const authHeaders = passedHeaders || adminAuth.authHeaders;
  const routeBase = basePath || '/admin';
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [category, setCategory] = useState('prayer');
  const [reason, setReason] = useState('');
  const [elapsed, setElapsed] = useState(0);
  // Aug 2026 — Unsuccess-return modal
  const [failModalOpen, setFailModalOpen] = useState(false);
  const [failReason, setFailReason] = useState('');
  const tickRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}${routeBase}/desk/current`, { headers: authHeaders });
      setState(data);
    } catch (e) {
      // silent — endpoint may 401 during a stale token; caller will retry
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => { load(); }, [load]);

  // Live ticker for the "You've been away for Xm" counter when an
  // open desk record exists.
  useEffect(() => {
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    if (!state?.open_row?.out_at) { setElapsed(0); return; }
    const outTs = new Date(state.open_row.out_at).getTime();
    const tick = () => setElapsed(Math.max(0, Math.floor((Date.now() - outTs) / 60000)));
    tick();
    tickRef.current = setInterval(tick, 15000);
    return () => { if (tickRef.current) clearInterval(tickRef.current); };
  }, [state?.open_row?.out_at]);

  const openModal = () => {
    setCategory('prayer');
    setReason('');
    setModalOpen(true);
  };

  const submitLeave = async () => {
    if (category === 'other' && !reason.trim()) {
      toast.error('Please describe why you are leaving the desk.');
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}${routeBase}/desk/leave`, {
        category, reason: reason.trim(),
      }, { headers: authHeaders });
      toast.success('Desk marked as left. Timer running.');
      setModalOpen(false);
      await load();
      onChanged?.();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Failed to leave desk';
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const submitReturn = async (outcome, extraReason = '') => {
    setBusy(true);
    try {
      const body = { outcome };
      if (outcome === 'unsuccess') body.failure_reason = extraReason.trim();
      const { data } = await axios.post(`${API}${routeBase}/desk/return`, body, { headers: authHeaders });
      const label = outcome === 'success' ? 'Success' : 'Unsuccess';
      toast.success(`Welcome back! Away ${data.duration_display} · ${label}.`);
      setFailModalOpen(false);
      setFailReason('');
      await load();
      onChanged?.();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Failed to return to desk';
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const submitUnsuccessReturn = () => {
    if (!failReason.trim()) {
      toast.error('Please describe why the task was unsuccessful.');
      return;
    }
    submitReturn('unsuccess', failReason);
  };

  if (loading) {
    return (
      <div className="inline-flex items-center gap-2 text-xs text-slate-500" data-testid="desk-button-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading desk state…
      </div>
    );
  }

  // Feb 2026 — Leave-Desk / Return-to-Desk widget is available
  // 24/7, even outside duty hours. The backend allowlists these
  // endpoints (`/api/admin/desk/…` and `/api/branch/desk/…`) so
  // ops can record outdoor activity around the clock.
  const open = state?.open_row;

  if (open) {
    return (
      <>
        <div className="inline-flex items-center gap-2 flex-wrap" data-testid="desk-button-open">
          <div className={`inline-flex items-center gap-2 rounded-full ${compact ? 'px-3 py-1.5 text-xs' : 'px-4 py-2 text-sm'} bg-amber-50 text-amber-900 font-semibold border border-amber-200`}>
            <LogOut className="w-4 h-4" />
            Away · <span className="font-mono">{open.category_label}</span>
            <span className="text-amber-700">· {elapsed}m</span>
          </div>
          <div className="inline-flex items-center gap-2">
            <button
              type="button"
              onClick={() => submitReturn('success')}
              disabled={busy}
              className={`inline-flex items-center gap-2 rounded-full ${compact ? 'px-3 py-1.5 text-xs' : 'px-4 py-2 text-sm'} bg-emerald-600 hover:bg-emerald-700 text-white font-bold disabled:opacity-60 shadow-sm`}
              data-testid="desk-return-success"
              title="Return marking the task as successful"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              Success
            </button>
            <button
              type="button"
              onClick={() => { setFailReason(''); setFailModalOpen(true); }}
              disabled={busy}
              className={`inline-flex items-center gap-2 rounded-full ${compact ? 'px-3 py-1.5 text-xs' : 'px-4 py-2 text-sm'} bg-red-600 hover:bg-red-700 text-white font-bold disabled:opacity-60 shadow-sm`}
              data-testid="desk-return-unsuccess"
              title="Return marking the task as unsuccessful — a reason is required"
            >
              <XCircle className="w-4 h-4" />
              Unsuccess
            </button>
          </div>
        </div>

        {failModalOpen && (
          <div
            className="fixed inset-0 z-[1000] flex items-center justify-center bg-slate-900/70 backdrop-blur-sm p-4"
            role="dialog"
            aria-modal="true"
            data-testid="desk-unsuccess-modal"
          >
            <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
              <div className="bg-gradient-to-br from-[#7F1D1D] to-[#B91C1C] text-white px-5 py-4 flex items-start justify-between gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-widest font-bold opacity-80">Return · Unsuccess</div>
                  <div className="text-lg font-extrabold leading-snug">Reason (required)</div>
                </div>
                <button
                  type="button"
                  onClick={() => { setFailModalOpen(false); setFailReason(''); }}
                  className="text-white/70 hover:text-white text-lg leading-none"
                  aria-label="Close"
                  data-testid="desk-unsuccess-cancel"
                >
                  ×
                </button>
              </div>
              <div className="px-5 py-4 space-y-3">
                {/* Feb 2026 — Minimalist "reason + submit only" form.
                    No actor selection — the session identifies the
                    user automatically. */}
                <textarea
                  value={failReason}
                  onChange={(e) => setFailReason(e.target.value)}
                  placeholder="e.g. Bank counter closed for the day, retrying tomorrow…"
                  className="w-full min-h-[96px] rounded-lg border border-slate-300 p-3 text-sm focus:ring-2 focus:ring-red-500 focus:outline-none"
                  data-testid="desk-failure-reason-input"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={submitUnsuccessReturn}
                  disabled={busy || !failReason.trim()}
                  className="w-full h-12 rounded-full bg-red-600 hover:bg-red-700 text-white text-sm font-bold disabled:opacity-60 inline-flex items-center justify-center gap-2"
                  data-testid="desk-unsuccess-submit"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
                  Submit
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={openModal}
        className={`inline-flex items-center gap-2 rounded-full ${compact ? 'px-3 py-1.5 text-xs' : 'px-4 py-2 text-sm'} bg-slate-900 hover:bg-slate-950 text-white font-bold shadow-sm`}
        data-testid="desk-leave-button"
      >
        <LogOut className="w-4 h-4" /> Leave Desk
      </button>
      {modalOpen && (
        <div
          className="fixed inset-0 z-[1000] flex items-center justify-center bg-slate-900/70 backdrop-blur-sm p-4"
          role="dialog"
          aria-modal="true"
          data-testid="desk-leave-modal"
        >
          <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
            <div className="bg-gradient-to-br from-[#0F172A] to-[#0078D4] text-white px-5 py-4 flex items-start justify-between gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold opacity-70">Leave Desk</div>
                <div className="text-lg font-extrabold leading-snug">Pick a reason</div>
              </div>
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                className="text-white/70 hover:text-white text-lg leading-none"
                aria-label="Close"
                data-testid="desk-modal-cancel"
              >
                ×
              </button>
            </div>
            <div className="px-5 py-4 space-y-3">
              {/* Feb 2026 — Minimalist form per updated spec: ONLY the
                  reason picker and the submit button are visible. The
                  session identifies the actor automatically — no
                  Admin/Branch tab, no user dropdown, no free-text
                  description unless "Other" is selected (in which case
                  a compact input appears inline because the backend
                  requires a reason for the "other" category). */}
              <div className="flex flex-wrap gap-2">
                {CATEGORY_OPTIONS.map((c) => (
                  <button
                    key={c.key}
                    type="button"
                    onClick={() => setCategory(c.key)}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                      category === c.key
                        ? 'bg-sky-600 text-white border-sky-600'
                        : 'bg-white text-slate-700 border-slate-300 hover:border-sky-400'
                    }`}
                    data-testid={`desk-category-${c.key}`}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
              {category === 'other' && (
                <input
                  type="text"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Describe why you are leaving…"
                  className="w-full h-11 rounded-lg border border-slate-300 px-3 text-sm focus:ring-2 focus:ring-sky-500 focus:outline-none"
                  data-testid="desk-reason-input"
                  autoFocus
                />
              )}
              <button
                type="button"
                onClick={submitLeave}
                disabled={busy || (category === 'other' && !reason.trim())}
                className="w-full h-12 rounded-full bg-slate-900 hover:bg-slate-950 text-white text-sm font-bold disabled:opacity-60 inline-flex items-center justify-center gap-2"
                data-testid="desk-modal-submit"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogOut className="w-4 h-4" />}
                Submit
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DeskButton;
